import { MarkerType, type Edge, type Node } from "@xyflow/react";
import type { Pipeline, PipelineObjectType } from "../hooks/usePipelines";

const GROUP_PADDING_X = 24;
const GROUP_PADDING_TOP = 56;
const GROUP_PADDING_BOTTOM = 24;
const NODE_WIDTH = 160;
const NODE_HEIGHT = 160;
const STAGE_GAP = 96;

export interface PipelineCanvasItemPlacement {
  portalId: string;
  objectType: PipelineObjectType;
  pipelineId: string;
  positionX: number;
  positionY: number;
}

export interface BuildResult {
  nodes: Node[];
  edges: Edge[];
}

function key(objectType: PipelineObjectType, pipelineId: string): string {
  return `${objectType}:${pipelineId}`;
}

export function buildPipelineCanvasGraph(
  items: PipelineCanvasItemPlacement[],
  definitions: Record<string, Pipeline | undefined>,
): BuildResult {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  for (const item of items) {
    const def = definitions[key(item.objectType, item.pipelineId)];
    if (!def) continue;

    const stages = def.stages ?? [];
    const groupId = `pipeline:${item.objectType}:${item.pipelineId}`;

    const innerWidth =
      stages.length > 0
        ? stages.length * NODE_WIDTH + (stages.length - 1) * STAGE_GAP
        : NODE_WIDTH;
    const innerHeight = NODE_HEIGHT;

    nodes.push({
      id: groupId,
      type: "pipelineGroup",
      position: { x: item.positionX, y: item.positionY },
      data: {
        objectType: item.objectType,
        pipelineId: item.pipelineId,
        name: def.label,
      },
      style: {
        width: innerWidth + GROUP_PADDING_X * 2,
        height: innerHeight + GROUP_PADDING_TOP + GROUP_PADDING_BOTTOM,
      },
      draggable: true,
      selectable: true,
    });

    stages.forEach((stage, i) => {
      const stageNodeId = `${item.objectType}:${item.pipelineId}::${stage.id}`;
      nodes.push({
        id: stageNodeId,
        type: "stage",
        parentId: groupId,
        extent: "parent",
        position: {
          x: GROUP_PADDING_X + i * (NODE_WIDTH + STAGE_GAP),
          y: GROUP_PADDING_TOP,
        },
        data: {
          label: stage.label,
          probability: stage.metadata?.probability,
        },
        draggable: false,
        selectable: false,
      });
      if (i > 0) {
        const prev = stages[i - 1];
        edges.push({
          id: `${item.objectType}:${item.pipelineId}::${prev.id}->${stage.id}`,
          source: `${item.objectType}:${item.pipelineId}::${prev.id}`,
          target: stageNodeId,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 18,
            height: 18,
            color: "#6b6f80",
          },
          style: { stroke: "#6b6f80", strokeWidth: 1.5 },
        });
      }
    });
  }

  return { nodes, edges };
}
